import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '@redux/hooks';
import {
  setOrganizationData,
  clearOrganizationData,
} from '@redux/reducers/organization.reducer';
import { setLocale } from '@redux/reducers/locale.reducer';
import {
  logoutUser,
  setUserData,
  setUserDetails,
} from '@redux/reducers/user.reducer';
import { getSelfWithOrganizationRequest, UserLocale } from '@models/user.model';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import authService from '@services/auth';
import {
  setTokenLocalStorage,
  setTokenSessionStorage,
  removeResetPasswordEmail,
} from '@config/storage';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';
import type en from '@locales/en';
/** The literal the backend returns on a failed sign-in; never shown to the user as-is. */
export const AUTH_FAILED_BACKEND_MESSAGE = 'Authentication failed';

type ActionMessages = (typeof en)['actionMessages'];

/**
 * Sprint 52, story-265. Copy for a sign-in response that carried no token. Prefers the backend's
 * typed `errorCode`; an older backend without one falls back to today's behaviour (the literal
 * "Authentication failed" becomes the credentials copy, other messages pass through).
 */
export function signInFailureMessage(
  data: { errorCode?: string; errorMessage?: string } | undefined,
  t: ActionMessages,
): string {
  switch (data?.errorCode) {
    case 'PASSWORD_RESET_REQUIRED':
      return t.loginPasswordResetRequired;
    case 'USER_NOT_CONFIRMED':
      return t.loginUserNotConfirmed;
    case 'TEMP_PASSWORD_EXPIRED':
      return t.loginTempPasswordExpired;
    case 'INVALID_CREDENTIALS':
    case 'USER_DELETED':
      return t.checkYourCredentials;
    default:
      return data?.errorMessage === AUTH_FAILED_BACKEND_MESSAGE
        ? t.checkYourCredentials
        : data?.errorMessage || t.requestError;
  }
}

/** Copy for a sign-in request that threw: throttled (429), unreachable (no response) or otherwise failed. */
export function signInThrownMessage(e: any, t: ActionMessages): string {
  const status = e?.response?.status;
  if (status === 429) return t.loginTooManyAttempts;
  if (!e?.response) return t.loginCouldNotReachServer;
  if (status === 401 || status === 403) return t.checkYourCredentials;
  return t.requestError;
}

type LoginRequiresPasswordChange = {
  status: 'REQUIRES_PASSWORD_CHANGE';
  confirmToken: string;
};
type LoginSuccess = {
  status: 'SUCCESS';
};
type LoginFailure = { status: 'FAILURE'; errorMessage?: string };
type LoginResponse = LoginRequiresPasswordChange | LoginSuccess | LoginFailure;

export type SignInParams = {
  user: string;
  password: string;
  rememberMe: boolean;
};

type LoginResult = {
  signIn: (params: SignInParams) => Promise<void | LoginResponse>;
  isLoading: boolean;
  confirmInitialSignIn: (
    email: string,
    password: string,
    confirmToken: string,
    rememberMe: boolean,
  ) => Promise<void | LoginResponse>;
  /**
   * Sprint 47, story-232. Starts a session from an access token the backend already issued (the
   * one-time setup link's `complete` call), exactly as the login page does after sign-in.
   * `replace` swaps the history entry so Back never returns to a spent setup link.
   */
  loginWithToken: (
    token: string,
    rememberMe: boolean,
    replace?: boolean,
  ) => Promise<void>;
};

export const useLogin = (): LoginResult => {
  const tActionMessages = useTranslation('actionMessages');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const handleTokenLogin = useCallback(
    async (token: string, rememberMe: boolean, replace = false) => {
      const navigate = replace ? history.replace : history.push;
      // Clear any stored reset password email on successful login
      removeResetPasswordEmail();
      // Persist token (and rememberMe) in Redux and storage
      dispatch(setUserData(token, rememberMe));
      // Write token to both storages to support early requests before Redux rehydration in new tabs
      setTokenLocalStorage(token);
      setTokenSessionStorage(token);
      // Re-arm the dashboard's post-login "empty → first project" redirect.
      sessionStorage.removeItem('dashboardLandingHandled');

      // Fetch user + org using GraphQL
      const userData: getSelfWithOrganizationRequest =
        await graphQlClient.request(
          UserGraphQL.queries.getSelfWithOrganization,
        );

      // Populate Redux state - user details and locale
      dispatch(setUserDetails(userData.getSelfWithOrganization.user));
      dispatch(
        setLocale(
          userData.getSelfWithOrganization.user.locale || UserLocale.EN,
        ),
      );

      // Check if user has an organization
      if (!userData.getSelfWithOrganization.organization) {
        // Clear organization data from storage
        dispatch(clearOrganizationData());
        // Clear any redirect URL to prevent interference
        sessionStorage.removeItem('redirectUrl');
        navigate('/no-organization');
        return;
      }

      // User has organization - set it in Redux
      dispatch(
        setOrganizationData(userData.getSelfWithOrganization.organization),
      );

      // Redirect to saved URL or home
      const from = sessionStorage.getItem('redirectUrl') || '/';
      sessionStorage.removeItem('redirectUrl');
      if (from && from !== '/logout') {
        navigate(from);
      }
    },
    [dispatch, history],
  );

  const signIn = useCallback(
    async (params: SignInParams) => {
      setIsLoading(true);
      try {
        const data = await authService.signIn({
          user: params.user,
          password: params.password,
        });

        if (data?.message === 'FORCE_CHANGE_PASSWORD' && data?.confirmToken) {
          setIsLoading(false);
          toast.warning(tActionMessages.pleaseChangeYourPassword);
          const resp: LoginResponse = {
            status: 'REQUIRES_PASSWORD_CHANGE',
            confirmToken: data.confirmToken,
          };
          return resp;
        }

        if (data?.token) {
          // Clear any stored reset password email on successful login
          removeResetPasswordEmail();
          // Persist token first
          dispatch(setUserData(data.token, params.rememberMe));
          setTokenLocalStorage(data.token);
          setTokenSessionStorage(data.token);
          // Re-arm the dashboard's post-login "empty → first project" redirect.
          sessionStorage.removeItem('dashboardLandingHandled');

          // Fetch user data with default organization (backend handles default org selection)
          try {
            const userData: getSelfWithOrganizationRequest =
              await graphQlClient.request(
                UserGraphQL.queries.getSelfWithOrganization,
              );

            // Set user details and organization
            dispatch(setUserDetails(userData.getSelfWithOrganization.user));
            dispatch(
              setLocale(
                userData.getSelfWithOrganization.user.locale || UserLocale.EN,
              ),
            );

            // Check if user has an organization
            if (!userData.getSelfWithOrganization.organization) {
              // Clear organization data from storage
              dispatch(clearOrganizationData());
              setIsLoading(false);
              toast.success(tActionMessages.loggedInSuccessfully);
              // Clear any redirect URL to prevent interference
              sessionStorage.removeItem('redirectUrl');
              history.push('/no-organization');
              const resp: LoginResponse = { status: 'SUCCESS' };
              return resp;
            }

            // User has organization - set it in Redux
            dispatch(
              setOrganizationData(
                userData.getSelfWithOrganization.organization,
              ),
            );
            setIsLoading(false);
            toast.success(tActionMessages.loggedInSuccessfully);

            // Redirect to saved URL or home
            const from = sessionStorage.getItem('redirectUrl') || '/';
            sessionStorage.removeItem('redirectUrl');
            if (from && from !== '/logout') {
              history.push(from);
            }

            const resp: LoginResponse = { status: 'SUCCESS' };
            return resp;
          } catch (userError: any) {
            try {
              const tokenPayload = JSON.parse(atob(data.token.split('.')[1]));
              dispatch(
                setUserDetails({
                  email: tokenPayload.email,
                  firstName: tokenPayload.given_name || '',
                  lastName: tokenPayload.family_name || '',
                  userId: tokenPayload.sub,
                }),
              );
              // Clear organization data from storage
              dispatch(clearOrganizationData());
              setIsLoading(false);
              toast.success(tActionMessages.loggedInSuccessfully);
              // Clear any redirect URL to prevent interference
              sessionStorage.removeItem('redirectUrl');
              history.push('/no-organization');
              const resp: LoginResponse = { status: 'SUCCESS' };
              return resp;
            } catch (decodeError) {
              toast.error(tActionMessages.errorLoadingUserData);
              setIsLoading(false);
              const resp: LoginResponse = {
                status: 'FAILURE',
                errorMessage: 'Error loading user data',
              };
              return resp;
            }
          }
        }

        const errorMessage = signInFailureMessage(data, tActionMessages);

        toast.error(errorMessage);
        setIsLoading(false);
        const resp: LoginResponse = { status: 'FAILURE', errorMessage };
        return resp;
      } catch (e: any) {
        // Not a credentials problem: say what actually happened (throttled, unreachable, server fault).
        const errorMessage = signInThrownMessage(e, tActionMessages);
        toast.error(errorMessage);
        setIsLoading(false);
        const resp: LoginResponse = { status: 'FAILURE', errorMessage };
        return resp;
      }
    },
    [
      dispatch,
      history,
      tActionMessages,
    ],
  );

  const confirmInitialSignIn = useCallback(
    async (
      email: string,
      password: string,
      confirmToken: string,
      rememberMe: boolean,
    ) => {
      setIsLoading(true);
      try {
        const data = await authService.signInWithConfirm(
          email,
          password,
          confirmToken,
        );
        if (data?.token) {
          await handleTokenLogin(data.token, rememberMe);
          setIsLoading(false);
          toast.success(tActionMessages.passwordChangeSuccessful);
          const resp: LoginResponse = { status: 'SUCCESS' };
          return resp;
        }

        const errorMessage = data?.errorMessage || tActionMessages.requestError;
        toast.error(errorMessage);

        setIsLoading(false);
        const resp: LoginResponse = { status: 'FAILURE', errorMessage };
        return resp;
      } catch (e: any) {
        const errorMessage =
          e?.response?.data?.errorMessage || e?.message || tActionMessages.requestError;
        toast.error(tActionMessages.checkYourCredentials);
        dispatch(logoutUser());
        setIsLoading(false);
        const resp: LoginResponse = { status: 'FAILURE', errorMessage };
        return resp;
      }
    },
    [
      dispatch,
      handleTokenLogin,
      tActionMessages.passwordChangeSuccessful,
      tActionMessages.checkYourCredentials,
      tActionMessages.requestError,
    ],
  );

  return {
    signIn,
    confirmInitialSignIn,
    loginWithToken: handleTokenLogin,
    isLoading,
  };
};

export default useLogin;
